const SEGMENTATION_FLUIDS = ["IRF", "SRF", "PED", "SHRM"];
const SEGMENTATION_SIZES = ["1mm", "3mm", "6mm"];

const SEGMENTATION_REGION_BY_SIZE = {
  "1mm": "CSF",
  "3mm": "ISF",
  "6mm": "OSF",
};

const ETDRS_EMPTY_VALUES = {
  EZDA_ON_ETDRS_1MM: " ",
  EZDA_ON_ETDRS_3MM: " ",
  EZDA_ON_ETDRS_6MM: " ",
  ETDRS_ON_OCT: " ",
};

/**
 * Recursively map form field keys to values using a Form.io component attribute.
 */
export const findFormAttributeMatches = (
  components,
  values,
  attributeName,
  matches = {}
) => {
  components?.forEach((obj) => {
    const attributeValue = obj?.attributes?.[attributeName];
    if (attributeValue && values[attributeValue] != null) {
      matches[obj.key] = String(values[attributeValue]).trim();
    }

    if (Array.isArray(obj?.components)) {
      findFormAttributeMatches(obj.components, values, attributeName, matches);
    }
  });

  return matches;
};

export const findETDRSMatches = (components, values, matches = {}) =>
  findFormAttributeMatches(components, values, "data-etdrs", matches);

export const formatVolumeToThreeDecimals = (volume) => {
  if (volume == null || !Number.isFinite(Number(volume))) {
    return null;
  }

  return Number(volume).toFixed(3);
};

export const parseSegmentationAttribute = (attributeValue) => {
  if (!attributeValue || typeof attributeValue !== "string") {
    return null;
  }

  const match = /^([A-Za-z]+)_(\dmm)$/.exec(attributeValue);
  if (!match) {
    return null;
  }

  return {
    fluid: match[1].toUpperCase(),
    size: match[2].toLowerCase(),
  };
};

export const getSegmentationVolumeValue = (volumesData, attributeValue) => {
  const parsed = parseSegmentationAttribute(attributeValue);
  if (!parsed || !volumesData?.regions) {
    return null;
  }

  const regionKey = SEGMENTATION_REGION_BY_SIZE[parsed.size];
  if (!regionKey) {
    return null;
  }

  const volume = volumesData.regions[regionKey]?.[parsed.fluid]?.volume_mm3;
  return formatVolumeToThreeDecimals(volume);
};

export const buildSegmentationAttributeValues = (volumesData) => {
  if (!volumesData?.regions) {
    return {};
  }

  const values = {};

  SEGMENTATION_FLUIDS.forEach((fluid) => {
    SEGMENTATION_SIZES.forEach((size) => {
      const attributeValue = `${fluid}_${size}`;
      const formattedValue = getSegmentationVolumeValue(
        volumesData,
        attributeValue
      );

      if (formattedValue != null) {
        values[attributeValue] = formattedValue;
      }
    });
  });

  return values;
};

export const buildETDRSAttributeValues = (dicom_data, { active = false } = {}) => {
  if (!active) {
    return { ...ETDRS_EMPTY_VALUES };
  }

  const areaVersions = dicom_data?.lisonsAreasData?.area_versions;
  const pixelatedObject = areaVersions?.find(
    (obj) => obj.area_type === dicom_data?.lisonOptions?.lisonAreaselected
  );

  return {
    EZDA_ON_ETDRS_1MM: `${pixelatedObject?.lesions_1mm_area_mm2}  `,
    EZDA_ON_ETDRS_3MM: `${pixelatedObject?.lesions_3mm_area_mm2} `,
    EZDA_ON_ETDRS_6MM: `${pixelatedObject?.lesions_6mm_area_mm2} `,
    ETDRS_ON_OCT: `${pixelatedObject?.ETDRS_area_mm2} `,
  };
};

const hasValidETDRSMatches = (etdrsMatches) =>
  Object.keys(etdrsMatches).length > 0 &&
  Object.values(etdrsMatches).every((value) => value !== "undefined ");

/**
 * Build key/value pairs to merge into form submission data for ETDRS and
 * segmentation-driven fields present in the schema.
 */
export const getFormAutoFillMatches = (components, dicom_data) => {
  if (!components) {
    return {};
  }

  const matches = {};

  if (dicom_data?.etdrsActive) {
    const etdrsValues = buildETDRSAttributeValues(dicom_data, { active: true });
    const etdrsMatches = findETDRSMatches(components, etdrsValues);

    if (hasValidETDRSMatches(etdrsMatches)) {
      Object.assign(matches, etdrsMatches);
    }
  } else {
    const etdrsMatches = findETDRSMatches(components, ETDRS_EMPTY_VALUES);
    if (Object.keys(etdrsMatches).length > 0) {
      Object.assign(matches, etdrsMatches);
    }
  }

  const volumesData = dicom_data?.etdrsVolumesFromSegmentationApi;
  if (volumesData) {
    const segmentationValues = buildSegmentationAttributeValues(volumesData);
    const segmentationMatches = findFormAttributeMatches(
      components,
      segmentationValues,
      "data-segmentation"
    );
    Object.assign(matches, segmentationMatches);
  }

  return matches;
};

export const shouldUpdateFormAutoFill = (currentResponseData, matches) =>
  Object.keys(matches).length > 0 &&
  Object.entries(matches).some(
    ([key, value]) => currentResponseData?.[key] !== value
  );

export const mergeAutoFillIntoFormState = (
  prevState,
  matches,
  { dataPath = "response" } = {}
) => ({
  ...prevState,
  [dataPath]: {
    ...prevState[dataPath],
    data: {
      ...prevState[dataPath]?.data,
      ...matches,
    },
  },
});
